import { Building } from "../../../building/Building";
import { Config } from "../../../config/Config";
import { LeanToConfig } from "../../../config/LeanToConfig";
import { Mesh, Object3D, TextureLoader, Texture, EquirectangularReflectionMapping, LinearFilter, LinearMipMapLinearFilter, Vector3, MeshStandardMaterial } from "three";
import { Main } from "../../../Main";
import { PostManager } from "../../../building/post/PostManager";
import { LeanToWraparoundSetting } from "../../../config/LeanToWraparoundSetting";
import { WallAddon } from "../../addon/WallAddon";
import { THREEUtils } from "../../../utils/THREEUtils";

/**
 * The inside structure, support beam of the building that can be seen when inside.
 *
 * For more info:
 * https://docs.google.com/document/d/1j7_c1EgUqyiGFW-kkLespK27EeTX_T8PAwyPu28hnpY/edit?ts=579bc2f1
 *
 */
export class SupportBeam
{
	private _container: Object3D;
	private _building: Building;
	private _config: Config;
	private _scaleRatio: number;

	private _children: Mesh[];
	private _eave_strut_left: Mesh;
	private _eave_strut_right: Mesh;
	private _girtRight: Mesh;
	private _girtLeft: Mesh;
	private _girtFront: Mesh;
	private _girtBack: Mesh;
	private _girts: Mesh[];
	private _purlinLeft: Mesh;
	private _purlinRight: Mesh;
	private _purlins: Mesh[];
	private _singleCee: Mesh;
	private _singleCees: Mesh[];
	private _singleCeeLeftSide: Mesh;
	private _singleCeesLeftSide: Mesh[];
	private _singleCeeRightSide: Mesh;
	private _singleCeesRightSide: Mesh[];
	private _singleCeeFrontSide: Mesh;
	private _singleCeeBackSide: Mesh;
	private _singleCeesFrontSide: Mesh[];
	private _singleCeesBackSide: Mesh[];
	private _wideFlange: Mesh;
	private _wideFlangesLeftSide: Mesh[];
	private _wideFlangesRightSide: Mesh[];
	private _rollupBeams: Mesh[];
	private _leanToConfig: LeanToConfig;
	private _rightSideLeantoWideFlanges: Mesh[];
	private _leftSideLeantoWideFlanges: Mesh[];
	private _backSideLeantoWideFlanges: Mesh[];
	private _frontSideLeantoWideFlanges: Mesh[];
	private _rightSideLeantoPurlins: Mesh[];
	private _leftSideLeantoPurlins: Mesh[];
	private _backSideLeantoPurlins: Mesh[];
	private _frontSideLeantoPurlins: Mesh[];
	private _frontSideInsetBay_1_wideFlanges: Object3D;
	private _frontSideInsetBay_2_wideFlanges: Object3D;
	private _backSideInsetBay_1_wideFlanges: Object3D;
	private _backSideInsetBay_2_wideFlanges: Object3D;
	private _wideFlangeLength: number;
	private _rightSideLeantoEdge: Mesh;
	private _leftSideLeantoEdge: Mesh;
	private _backSideLeantoEdge: Mesh;
	private _frontSideLeantoEdge: Mesh;
	private _backLeftLeantoWrapWideFlange: Mesh;
	private _backRightLeantoWrapWideFlange: Mesh;
	private _frontLeftLeantoWrapWideFlange: Mesh;
	private _frontRightLeantoWrapWideFlange: Mesh;
	private _singleCeeHeight: number;
	private _purlinLength: number;
	private _beamMaterial: MeshStandardMaterial;

	private _envMap: Texture;

	private _scene: Object3D;

	constructor(building:Building)
	{
		this._building = building;
		this._config = building.config;
		this._container = new Object3D();
		this._girts = [];
		this._singleCees = [];
		this._purlins = [];
		this._wideFlangesLeftSide = [];
		this._wideFlangesRightSide = [];
		this._singleCeesLeftSide = [];
		this._singleCeesRightSide = [];
		this._singleCeesFrontSide = [];
		this._singleCeesBackSide = [];
		this._rollupBeams = [];
		this._scaleRatio = 3.26; // 1 m = 3.2808399 feet
		this._leanToConfig = Main.getInstance().config.building.leanto;
		this._rightSideLeantoWideFlanges = [];
		this._leftSideLeantoWideFlanges = [];
		this._backSideLeantoWideFlanges = [];
		this._frontSideLeantoWideFlanges = [];
		this._rightSideLeantoPurlins = [];
		this._leftSideLeantoPurlins = [];
		this._backSideLeantoPurlins = [];
		this._frontSideLeantoPurlins = [];
		this._frontSideInsetBay_1_wideFlanges = new Object3D();
		this._frontSideInsetBay_2_wideFlanges = new Object3D();
		this._backSideInsetBay_1_wideFlanges = new Object3D();
		this._backSideInsetBay_2_wideFlanges = new Object3D();
		this._wideFlangeLength = 3.472;
		this._singleCeeHeight = 3.471;
		this._purlinLength = 6.744;

		this._building.signals.addonsChange.add(this.onUpdate);
		this.initMaterialLoading();
		this.initModelLoading();
	}

	private initMaterialLoading()
	{
		const textureLoader = new TextureLoader();
		this._envMap = textureLoader.load("assets/images/beamenvironment.jpg", this.onTextureLoad);
	}

	private onTextureLoad = () =>
	{
		this._envMap.mapping = EquirectangularReflectionMapping;
		this._envMap.magFilter = LinearFilter;
		this._envMap.minFilter = LinearMipMapLinearFilter;
	};

	public toggleEnvMap(value:boolean)
	{
		if (value)
		{
			this._beamMaterial.envMap = this._envMap;
		}
		else
		{
			this._beamMaterial.envMap = null;
		}
		this._beamMaterial.needsUpdate = true;
	}

	private initModelLoading = () =>
	{
		const beams = this._config.gltfScene.getObjectByName("beams");
		this._children = <Mesh[]>beams.children;

		/*this._beamMaterial = new MeshStandardMaterial({
			color: 0xCC644F,
			metalness: 0.1,
			roughness: 0.35,
			envMap: Main.getInstance().envMapsToggler.checked ? this._envMap : null
		});*/
		this._beamMaterial = new MeshStandardMaterial({
			color: 0xCC644F,
			envMap: this._envMap,
			roughness: 0.2,
			metalness: 0.8
		});

		for (let i = 0; i < this._children.length; ++i)
		{
			this._children[i].material = this._beamMaterial;
			this._children[i].position.set(0.0, 0.0, 0.0);
			this._children[i].castShadow = false;
			this._children[i].receiveShadow = false;
		}

		this._eave_strut_left = <Mesh>beams.getObjectByName("eave_strut").clone();
		this._eave_strut_left.scale.set(9.85, this._scaleRatio, this._scaleRatio);
		this._eave_strut_left.rotateOnAxis(new Vector3(0, 1, 0), Math.PI/2);
		this._container.add(this._eave_strut_left);

		this._eave_strut_right = this._eave_strut_left.clone();
		this._eave_strut_left.rotateOnAxis(new Vector3(1, 0, 0), Math.PI);
		this._container.add(this._eave_strut_right);

		this._girtRight = <Mesh>beams.getObjectByName("girt").clone();
		this._girtRight.rotateOnAxis(new Vector3(0, 0, 1), Math.PI/2);

		this._girtLeft = <Mesh>beams.getObjectByName("girt").clone();
		this._girtLeft.rotateOnAxis(new Vector3(0, 1, 0),  Math.PI);
		this._girtLeft.rotateOnAxis(new Vector3(0, 0, 1),  Math.PI/2);

		this._girtFront = <Mesh>beams.getObjectByName("girt").clone();
		this._girtFront.rotateOnAxis(new Vector3(0, 1, 0), -Math.PI/2);
		this._girtFront.rotateOnAxis(new Vector3(0, 0, 1),  Math.PI/2);

		this._girtBack = <Mesh>beams.getObjectByName("girt").clone();
		this._girtBack.rotateOnAxis(new Vector3(0, 1, 0), Math.PI/2);
		this._girtBack.rotateOnAxis(new Vector3(0, 0, 1), Math.PI/2);

		this._wideFlange = <Mesh>beams.getObjectByName("wide_flange").clone(); // interior roof support

		this._singleCee = <Mesh>beams.getObjectByName("single_cee").clone();
														   // watching from "top": corner cees
		this._singleCees.push(this._singleCee.clone()); // top left
		this._singleCees.push(this._singleCee.clone()); // bottom left
		this._singleCees.push(this._singleCee.clone()); // bottom right
		this._singleCees.push(this._singleCee.clone()); // top right

														   // watching from "top": pitch cees
		this._singleCees.push(this._singleCee.clone()); // top left pitch
		this._singleCees.push(this._singleCee.clone()); // bottom left pitch
		this._singleCees.push(this._singleCee.clone()); // bottom right pitch
		this._singleCees.push(this._singleCee.clone()); // top right pitch [7]

		this._singleCees[1].rotateOnAxis(new Vector3(0, 1, 0), Math.PI); // bottom left corner
		this._singleCees[2].rotateOnAxis(new Vector3(0, 1, 0), Math.PI); // bottom right corner

		this._singleCeeLeftSide = this._singleCee.clone();
		this._singleCeeRightSide = this._singleCee.clone();
		this._singleCeeFrontSide = this._singleCee.clone();
		this._singleCeeBackSide = this._singleCee.clone();
		this._singleCeeLeftSide.rotateOnAxis(new Vector3(0, 1, 0), Math.PI/2); // left
		this._singleCeeRightSide.rotateOnAxis(new Vector3(0, 1, 0), -Math.PI/2); // right
		this._singleCeeFrontSide.rotateOnAxis(new Vector3(0, 1, 0), Math.PI); // front

		for (var i = 0; i < this._singleCees.length; ++i)
		{
			this._container.add(this._singleCees[i]);
		}


		this._purlinLeft = <Mesh>beams.getObjectByName("purlin").clone();
		this._purlinLeft.rotateOnAxis(new Vector3(0, 1, 0), -Math.PI/2);

		this._purlinRight = <Mesh>beams.getObjectByName("purlin").clone();
		this._purlinRight.rotateOnAxis(new Vector3(0, 1, 0), Math.PI/2);

		this._rightSideLeantoEdge = this._singleCee.clone();
		this._rightSideLeantoEdge.rotateOnAxis(new Vector3(0, 1, 0), Math.PI/2);
		this._rightSideLeantoEdge.rotateOnAxis(new Vector3(0, 0, 1), Math.PI/2);

		this._leftSideLeantoEdge = this._singleCee.clone();
		this._leftSideLeantoEdge.rotateOnAxis(new Vector3(0, 1, 0), -Math.PI/2);
		this._leftSideLeantoEdge.rotateOnAxis(new Vector3(0, 0, 1), Math.PI/2);

		this._backSideLeantoEdge = this._singleCee.clone();
		this._backSideLeantoEdge.rotateOnAxis(new Vector3(0, 1, 0), Math.PI);
		this._backSideLeantoEdge.rotateOnAxis(new Vector3(0, 0, 1), Math.PI/2);

		this._frontSideLeantoEdge = this._singleCee.clone();
		this._frontSideLeantoEdge.rotateOnAxis(new Vector3(0, 0, 1), Math.PI/2);

		this._backLeftLeantoWrapWideFlange = this._wideFlange.clone();
		this._backRightLeantoWrapWideFlange = this._wideFlange.clone();
		this._frontLeftLeantoWrapWideFlange = this._wideFlange.clone();
		this._frontRightLeantoWrapWideFlange = this._wideFlange.clone();


		this.onUpdate();
		Main.getInstance().render = true;

		this.addListeners();
	}
	public onUpdate = () =>
	{
		if (!this._children)
		{
			return;
		}
		if (!this._scene)
		{
			return;
		}

		if (!this._config.building.supportBeam.value)
		{
			if (this._container.parent === this._scene)
			{
				this._scene.remove(this._container);
			}

			return;
		}

		if (!this._container.parent)
		{
			this._scene.add(this._container);
		}

		var width = this._config.building.width.value;
		var height = this._config.building.height.value;
		var length = this._config.building.length.value;
		var pitch = this._config.building.pitch.value;

		//
		// Main building
		//
		this.setEaveStruts(width, height, length, pitch);
		this.setGirts(width, height, length, pitch);
		this.setSingleCees(width, height, length, pitch);
		this.setWideFlanges(width, height, length, pitch);
		this.setPurlins(width, height, length, pitch);
		this.setRollupBeams(width, height, length, pitch);
		//
		// End of main building
		//

		this.setLeanToBeams(width, height, length);

		this.setInsetBayBeams(width, height, length, pitch);
	}

	private setInsetBayBeams(width: number, height: number, length: number, pitch: number)
	{
		var insetBay = this._config.building.insetBay;

		this.eraseContainer(this._frontSideInsetBay_1_wideFlanges);
		this.eraseContainer(this._frontSideInsetBay_2_wideFlanges);
		this.eraseContainer(this._backSideInsetBay_1_wideFlanges);
		this.eraseContainer(this._backSideInsetBay_2_wideFlanges);

		if (insetBay.front.count.value >= 1)
		{
			this.updateInsetBayBeams(Building.WALLS.FRONT, 1, this._frontSideInsetBay_1_wideFlanges, width, height, length, pitch);
		}
		if (insetBay.front.count.value >= 2) //actually it can't be > 2, but maybe it changes later
		{
			this.updateInsetBayBeams(Building.WALLS.FRONT, 2, this._frontSideInsetBay_2_wideFlanges, width, height, length, pitch);
		}

		if (insetBay.back.count.value >= 1)
		{
			this.updateInsetBayBeams(Building.WALLS.BACK, 1, this._backSideInsetBay_1_wideFlanges, width, height, length, pitch);
		}
		if (insetBay.back.count.value >= 2)
		{
			this.updateInsetBayBeams(Building.WALLS.BACK, 2, this._backSideInsetBay_2_wideFlanges, width, height, length, pitch);
		}
	}
	private eraseContainer(container: Object3D)
	{
		for (var i = container.children.length - 1; i >= 0; --i)
		{
			var child = container.children[i];
			container.remove(child);
		}
		this._container.remove(container);
	}

	private updateInsetBayBeams(whichWall: string, id: number, container: Object3D, buildingWidth: number, buildingHeight: number, buildingLength: number, pitch: number)
	{
		var insetBays = this._config.building.insetBay;

		var bay;
		if (whichWall === Building.WALLS.FRONT)
		{
			bay = insetBays.front;
		}
		else if (whichWall === Building.WALLS.BACK)
		{
			bay = insetBays.back;
			container.rotation.y = Math.PI;
		}
		var roofLength = id === 1 ? bay.length_1.value : bay.length_2.value;
		var drop = 0;//bay.drop.value;

		var insetBayPositionZ; // The center of the bay

		if (id === 1)
		{
			insetBayPositionZ = buildingLength / 2 + roofLength / 2;
		}
		else // (if id === 2)
		{
			insetBayPositionZ = buildingLength / 2 + bay.length_1.value + roofLength / 2;
		}

		var fineTune = 1.1;
		var numberOfBays = Math.ceil(roofLength / PostManager.IntervalOnSide);
		var requiredPosts = numberOfBays + 1; // numberofbays + 1 - 2 (the columns for 2 endpoints are single cees, and are constructed elsewhere)

		var angleForLeft = this.getAngleFromPitch();
		var angleForRight = angleForLeft;

		var endOffset = 0;
		var plusHeight = (buildingWidth / 2)*(pitch/12);
		var roofWidth = Math.sqrt(plusHeight*plusHeight + (buildingWidth / 2)*(buildingWidth / 2)) - 0.1; // half of the roof

		var singleSlopeOffsetX = 0;
		var singleSlopeOffsetY = 0;

		if (this._config.building.single_slope.value)
		{
			angleForRight *= -1;
			singleSlopeOffsetX += buildingWidth/50 - 0.1;
			singleSlopeOffsetY += (buildingWidth*pitch) / 24;

			if (whichWall === Building.WALLS.BACK)
			{
				angleForRight *= -1;
				angleForLeft *= -1;
			}
		}
		for (var i = 0; i < requiredPosts; ++i)
		{
			if (i === 0)
			{
				endOffset = 0.25;
				continue;
			}
			else if (i === requiredPosts - 1)
			{
				endOffset = -endOffset;
			}
			var rightSideWideFlange = this._wideFlange.clone();

			var x = -buildingWidth / 4 + buildingWidth / 105;
			var y = buildingHeight + (buildingWidth * pitch / 48) - drop - fineTune;
			var z = insetBayPositionZ + (-roofLength / 2 + i / (requiredPosts - 1) * roofLength) + endOffset;

			var wideFlangeFinalLength = roofWidth / this._wideFlangeLength;

			var leftSideWideFlange = this._wideFlange.clone();




			leftSideWideFlange.position.set(x, y, z);
			leftSideWideFlange.scale.set(this._scaleRatio, this._scaleRatio, wideFlangeFinalLength);

			x = -x;

			rightSideWideFlange.position.set(x, y, z);
			rightSideWideFlange.scale.set(this._scaleRatio, this._scaleRatio, wideFlangeFinalLength);

			if (Main.getInstance().config.building.single_slope.value)
			{
				var wideFlange: Object3D;

				if (whichWall === Building.WALLS.FRONT)
				{
					wideFlange = rightSideWideFlange;
					wideFlange.position.x += singleSlopeOffsetX;
				}
				else
				{
					wideFlange = leftSideWideFlange;
					wideFlange.position.x -= singleSlopeOffsetX;
				}
				wideFlange.position.y += singleSlopeOffsetY;
			}

			leftSideWideFlange.rotateOnAxis(new Vector3(0, 0, 1), Math.PI / 2);
			leftSideWideFlange.rotateOnAxis(new Vector3(1, 0, 0), angleForLeft);
			rightSideWideFlange.rotateOnAxis(new Vector3(0, 0, 1), -Math.PI / 2);
			rightSideWideFlange.rotateOnAxis(new Vector3(1, 0, 0), angleForRight);

			container.add(leftSideWideFlange);
			container.add(rightSideWideFlange);

		}

		//
		// Purlins
		//

		var purlinInterval = 5; //feet
		var fineTune = 0.45;
		var currentOffset = 1;

		while (currentOffset < buildingWidth / 2 - 2)
		{
			var height = this._config.metrics.top - Math.cos(this.getAngleFromPitch(pitch))*currentOffset - drop - fineTune;

			var purlinLeft = this._purlinLeft.clone();
			//			   x   z   y
			purlinLeft.scale.set(roofLength/this._purlinLength, this._scaleRatio, this._scaleRatio);
			purlinLeft.position.set(-currentOffset, height, insetBayPositionZ);

			var purlinRight = this._purlinRight.clone();
			//			   x   z   y
			purlinRight.scale.set(roofLength/this._purlinLength, this._scaleRatio, this._scaleRatio);
			purlinRight.position.set(currentOffset, height, insetBayPositionZ);

			if (Main.getInstance().config.building.single_slope.value)
			{
				var purlin;
				if (whichWall === Building.WALLS.FRONT)
				{
					purlin = purlinRight;
				}
				else
				{
					purlin = purlinLeft;
				}
				purlin.position.y = this._config.metrics.top + Math.cos(this.getAngleFromPitch(pitch))*currentOffset - fineTune;
			}

			container.add(purlinLeft);
			container.add(purlinRight);

			currentOffset += purlinInterval;
		}

		//
		// Left and right single cees
		//
		var leftSingleCee = this._singleCeeRightSide.clone();
		var rightSingleCee = this._singleCeeLeftSide.clone();

		leftSingleCee.scale.set(this._scaleRatio, this._scaleRatio, roofLength/this._singleCeeHeight);
		leftSingleCee.rotation.x = 0;
		leftSingleCee.position.set(-buildingWidth/2 + 0.3, buildingHeight - drop - 0.36, insetBayPositionZ);

		rightSingleCee.scale.set(this._scaleRatio, this._scaleRatio, roofLength/this._singleCeeHeight);
		rightSingleCee.rotation.x = 0;
		rightSingleCee.position.set(buildingWidth/2 - 0.3, buildingHeight - drop - 0.36, insetBayPositionZ);

		if (Main.getInstance().config.building.single_slope.value)
		{
			var singleCee;
			if (whichWall === Building.WALLS.FRONT)
			{
				singleCee = rightSingleCee;
			}
			else
			{
				singleCee = leftSingleCee;
			}
			singleCee.position.y = this._config.metrics.top + Math.cos(this.getAngleFromPitch(pitch))*Math.abs(singleCee.position.x) - fineTune;
		}

		container.add(leftSingleCee);
		container.add(rightSingleCee);


		this._container.add(container);

	}

	private setLeanToBeams(width, height, length)
	{
		var buildingLength = length;

		this.setBackSideLeantoWideFlanges(width, height, length);
		this.setBackSideLeantoPurlins(width, height, length);
		this.setBackSideLeantoEdge(width, height, length);

		this.setFrontSideLeantoWideFlanges(width, height, length);
		this.setFrontSideLeantoPurlins(width, height, length);
		this.setFrontSideLeantoEdge(width, height, length);

		var insetBays = Main.getInstance().config.building.insetBay;
		var frontBays = insetBays.front;
		var backBays  = insetBays.back;

		var zPosOffset = 0;
		var zScaleOffset = 0;
		var zOffsetForWrapBeamsFront = 0;
		var zOffsetForWrapBeamsBack  = 0;

		if (frontBays.count.value >= 1)
		{
			zPosOffset += frontBays.length_1.value / 2;
			zScaleOffset += frontBays.length_1.value;
			zOffsetForWrapBeamsFront += frontBays.length_1.value;
		}
		if (frontBays.count.value >= 2)
		{
			zPosOffset += frontBays.length_2.value / 2;
			zScaleOffset += frontBays.length_2.value;
			zOffsetForWrapBeamsFront += frontBays.length_2.value;
		}
		if (backBays.count.value >= 1)
		{
			zPosOffset -= backBays.length_1.value / 2;
			zScaleOffset += backBays.length_1.value;
			zOffsetForWrapBeamsBack += backBays.length_1.value;
		}
		if (backBays.count.value >= 2)
		{
			zPosOffset -= backBays.length_2.value / 2;
			zScaleOffset += backBays.length_2.value;
			zOffsetForWrapBeamsBack += backBays.length_2.value;
		}

		length += zScaleOffset;

		this.setRightSideLeantoWideFlanges(width, height, buildingLength, 0);
		this.setRightSideLeantoPurlins(width, height, length, zPosOffset);
		this.setRightSideLeantoEdge(width, height, length, zPosOffset);

		this.setLeftSideLeantoWideFlanges(width, height, buildingLength, 0);
		this.setLeftSideLeantoPurlins(width, height, length, zPosOffset);
		this.setLeftSideLeantoEdge(width, height, length, zPosOffset);

		length = buildingLength / 2;
		this.setWrapWideFlange("back-left",   this._leanToConfig.wraparound_back_left,   this._backLeftLeantoWrapWideFlange,   width, height, length + zOffsetForWrapBeamsBack);
		this.setWrapWideFlange("back-right",  this._leanToConfig.wraparound_back_right,  this._backRightLeantoWrapWideFlange,  width, height, length + zOffsetForWrapBeamsBack);
		this.setWrapWideFlange("front-left",  this._leanToConfig.wraparound_front_left,  this._frontLeftLeantoWrapWideFlange,  width, height, length + zOffsetForWrapBeamsFront);
		this.setWrapWideFlange("front-right", this._leanToConfig.wraparound_front_right, this._frontRightLeantoWrapWideFlange, width, height, length + zOffsetForWrapBeamsFront);
	}

	private setWrapWideFlange(which: "back-left" | "back-right" | "front-left" | "front-right", wraparound:LeanToWraparoundSetting, wideFlange:Mesh, width:number, height:number, length:number)
	{
		this._container.remove(wideFlange);
		if (wraparound.enabled.value)
		{
			var x:number, y:number, z:number, wideFlangeLength:number;
			switch (which)
			{
				case "back-left":
					x = -width/2 - this._leanToConfig.back.length.value/2 + 0.3;
					z = -length - this._leanToConfig.back.length.value/2 + 0.3;
					var h = this._leanToConfig.back.pitch.value*this._leanToConfig.back.length.value/24;
					y = height - h - this._leanToConfig.back.drop.value - 1.05;
					wideFlangeLength = Math.sqrt(2)*this._leanToConfig.back.length.value*0.95;
					this._backLeftLeantoWrapWideFlange = this._wideFlange.clone();
					wideFlange = this._backLeftLeantoWrapWideFlange;
					wideFlange.rotateOnAxis(new Vector3(0, 1, 0), Math.PI/4);
					wideFlange.rotateOnAxis(new Vector3(-1, 0, 0), Math.PI/2 + Math.atan(this._leanToConfig.back.pitch.value/(12*Math.sqrt(2))));
					break;
				case "back-right":
					x = width/2 + this._leanToConfig.back.length.value/2 - 0.3;
					z = -length - this._leanToConfig.back.length.value/2 + 0.3;
					var h = this._leanToConfig.back.pitch.value*this._leanToConfig.back.length.value/24;
					y = height - h - this._leanToConfig.back.drop.value - 1.05;
					wideFlangeLength = Math.sqrt(2)*this._leanToConfig.back.length.value*0.95;
					this._backRightLeantoWrapWideFlange = this._wideFlange.clone();
					wideFlange = this._backRightLeantoWrapWideFlange;
					wideFlange.rotateOnAxis(new Vector3(0, 1, 0), -Math.PI/4);
					wideFlange.rotateOnAxis(new Vector3(-1, 0, 0), Math.PI/2 + Math.atan(this._leanToConfig.back.pitch.value/(12*Math.sqrt(2))));
					break;
				case "front-left":
					x = -width/2 - this._leanToConfig.front.length.value/2 + 0.3;
					z = length + this._leanToConfig.front.length.value/2 - 0.3;
					var h = this._leanToConfig.front.pitch.value*this._leanToConfig.front.length.value/24;
					y = height - h - this._leanToConfig.front.drop.value - 1.05;
					wideFlangeLength = Math.sqrt(2)*this._leanToConfig.front.length.value*0.95;
					this._frontLeftLeantoWrapWideFlange = this._wideFlange.clone();
					wideFlange = this._frontLeftLeantoWrapWideFlange;
					wideFlange.rotateOnAxis(new Vector3(0, 1, 0), 3*Math.PI/4);
					wideFlange.rotateOnAxis(new Vector3(1, 0, 0), Math.PI/2 - Math.atan(this._leanToConfig.front.pitch.value/(12*Math.sqrt(2))));
					break;
				case "front-right":
					x = width/2 + this._leanToConfig.front.length.value/2 - 0.3;
					z = length + this._leanToConfig.front.length.value/2 - 0.3;
					var h = this._leanToConfig.front.pitch.value*this._leanToConfig.front.length.value/24;
					y = height - h - this._leanToConfig.front.drop.value - 1.05;
					wideFlangeLength = Math.sqrt(2)*this._leanToConfig.front.length.value*0.95;
					this._frontRightLeantoWrapWideFlange = this._wideFlange.clone();
					wideFlange = this._frontRightLeantoWrapWideFlange;
					wideFlange.rotateOnAxis(new Vector3(0, 1, 0), -3*Math.PI/4);
					wideFlange.rotateOnAxis(new Vector3(1, 0, 0), Math.PI/2 - Math.atan(this._leanToConfig.front.pitch.value/(12*Math.sqrt(2))));
					break;
			}
			wideFlange.scale.set(this._scaleRatio, wideFlangeLength/this._wideFlangeLength,  this._scaleRatio);
			wideFlange.position.set(x, y, z);

			this._container.add(wideFlange);
		}
	}

	private setFrontSideLeantoPurlins(width, height, length)
	{
		this.eraseMeshArray(this._frontSideLeantoPurlins);
		this.addFrontSideLeantoPurlins(width, height, length);
	}
	private addFrontSideLeantoPurlins(width, height, length)
	{
		if (this._leanToConfig.front.enabled.value)
		{
			var bays = Main.getInstance().config.building.insetBay.front;

			var zOffset = 0;


			if (bays.count.value >= 1)
			{
				zOffset += bays.length_1.value;
			}
			if (bays.count.value >= 2)
			{
				zOffset += bays.length_2.value;
			}

			var currentOffset = 1;
			var interval = 5;
			var z = width - (this._leanToConfig.front.cut_back.value + this._leanToConfig.front.cut_front.value);

			while (currentOffset < this._leanToConfig.front.length.value - 1) {
				var purlin = this._purlinLeft.clone();
				purlin.rotateOnAxis(new Vector3(0, 1, 0), Math.PI/2);
				var h = this._leanToConfig.front.pitch.value * currentOffset / 12;
				var y = height - h - this._leanToConfig.front.drop.value - 0.35;

				var wrapAppendix = 0;
				var wrapOffset = 0;
				if (this._leanToConfig.wraparound_front_left.enabled.value)
				{
					wrapAppendix += currentOffset
					wrapOffset -= currentOffset / 2;
				}
				if (this._leanToConfig.wraparound_front_right.enabled.value)
				{
					wrapAppendix += currentOffset
					wrapOffset += currentOffset / 2;
				}

				purlin.scale.set((z+wrapAppendix) / this._purlinLength, this._scaleRatio, this._scaleRatio);
				purlin.position.set(wrapOffset - ((width / 2 - this._leanToConfig.front.cut_back.value) + (-width / 2 + this._leanToConfig.front.cut_front.value)) / 2, y, zOffset + length / 2 + currentOffset);

				this._frontSideLeantoPurlins.push(purlin);

				currentOffset += interval;
			}

			for (var i = 0; i < this._frontSideLeantoPurlins.length; ++i) {
				this._container.add(this._frontSideLeantoPurlins[i]);
			}
		}
	}

	private setFrontSideLeantoEdge(width, height, length)
	{
		this._container.remove(this._frontSideLeantoEdge);
		if (this._leanToConfig.front.enabled.value)
		{
			var bays = Main.getInstance().config.building.insetBay.front;

			var zOffset = 0;


			if (bays.count.value >= 1)
			{
				zOffset += bays.length_1.value;
			}
			if (bays.count.value >= 2)
			{
				zOffset += bays.length_2.value;
			}

			var h = this._leanToConfig.front.pitch.value*this._leanToConfig.front.length.value/12;
			var y = height - h - this._leanToConfig.front.drop.value - 0.35;
			var z = width - (this._leanToConfig.front.cut_back.value + this._leanToConfig.front.cut_front.value);
			var wrapAppendix = 0;
			var wrapOffset = 0;
			if (this._leanToConfig.wraparound_front_left.enabled.value)
			{
				wrapAppendix += this._leanToConfig.left.length.value;
				wrapOffset -= this._leanToConfig.left.length.value/2;
			}
			if (this._leanToConfig.wraparound_front_right.enabled.value)
			{
				wrapAppendix += this._leanToConfig.right.length.value;
				wrapOffset += this._leanToConfig.right.length.value/2;
			}
			this._frontSideLeantoEdge.scale.set(this._scaleRatio, (z+wrapAppendix)/this._singleCeeHeight, this._scaleRatio);
			this._frontSideLeantoEdge.position.set(wrapOffset - ((width/2-this._leanToConfig.front.cut_back.value) + (-width/2 + this._leanToConfig.front.cut_front.value))/2, y, zOffset + length/2 + this._leanToConfig.front.length.value - 0.3);

			this._container.add(this._frontSideLeantoEdge);
		}
	}

	private setFrontSideLeantoWideFlanges(width, height, length)
	{
		this.eraseMeshArray(this._frontSideLeantoWideFlanges);
		this.addFrontSideLeantoWideFlanges(width, height, length);
	}
	private addFrontSideLeantoWideFlanges(width, height, length)
	{
		if (this._leanToConfig.front.enabled.value)
		{
			var bays = Main.getInstance().config.building.insetBay.front;

			var zOffset = 0;


			if (bays.count.value >= 1)
			{
				zOffset += bays.length_1.value;
			}
			if (bays.count.value >= 2)
			{
				zOffset += bays.length_2.value;
			}

			var numberOfBays = Math.ceil((width - this._leanToConfig.front.cut_back.value - this._leanToConfig.front.cut_front.value) / PostManager.IntervalOnEnd);
			var requiredPosts = numberOfBays + 1;

			for (var i = 0; i < requiredPosts; ++i)
			{
				var x = -width/2+this._leanToConfig.front.cut_back.value + i / (requiredPosts-1) * (width-this._leanToConfig.front.cut_front.value-this._leanToConfig.front.cut_back.value);
				var h = this._leanToConfig.front.pitch.value*this._leanToConfig.front.length.value/24;
				var y = height - h - this._leanToConfig.front.drop.value - 1;
				var z = length/2 + this._leanToConfig.front.length.value/2 - 0.45 + zOffset;

				var fineTuneForEnds = 0;
				if (i == 0)
				{
					fineTuneForEnds = -0.4;
				}
				else if (i == requiredPosts - 1)
				{
					fineTuneForEnds = 0.4;
				}

				var wideFlange = this._wideFlange.clone();
				wideFlange.rotateOnAxis(new Vector3(1, 0, 0), -1.01*this.getAngleFromPitch(this._leanToConfig.front.pitch.value));
				wideFlange.scale.set(this._scaleRatio, Math.sqrt(this._leanToConfig.front.length.value*this._leanToConfig.front.length.value+h*h)/this._wideFlangeLength-0.15, this._scaleRatio);
				wideFlange.position.set(x-fineTuneForEnds, y, z);



				this._frontSideLeantoWideFlanges.push(wideFlange);
			}

			for (var i = 0; i < this._frontSideLeantoWideFlanges.length; ++i)
			{
				this._container.add(this._frontSideLeantoWideFlanges[i]);
			}
		}
	}

	private setBackSideLeantoPurlins(width, height, length)
	{
		this.eraseMeshArray(this._backSideLeantoPurlins);
		this.addBackSideLeantoPurlins(width, height, length);
	}
	private addBackSideLeantoPurlins(width, height, length)
	{
		if (this._leanToConfig.back.enabled.value)
		{
			var bays = Main.getInstance().config.building.insetBay.back;

			var zOffset = 0;


			if (bays.count.value >= 1)
			{
				zOffset += bays.length_1.value;
			}
			if (bays.count.value >= 2)
			{
				zOffset += bays.length_2.value;
			}

			var currentOffset = 1;
			var interval = 5;
			var z = width - (this._leanToConfig.back.cut_back.value + this._leanToConfig.back.cut_front.value);

			while (currentOffset < this._leanToConfig.back.length.value - 1) {
				var purlin = this._purlinLeft.clone();
				purlin.rotateOnAxis(new Vector3(0, 1, 0), -Math.PI/2);
				var h = this._leanToConfig.back.pitch.value * currentOffset / 12;
				var y = height - h - this._leanToConfig.back.drop.value - 0.35;

				var wrapAppendix = 0;
				var wrapOffset = 0;
				if (this._leanToConfig.wraparound_back_left.enabled.value)
				{
					wrapAppendix += currentOffset
					wrapOffset -= currentOffset / 2;
				}
				if (this._leanToConfig.wraparound_back_right.enabled.value)
				{
					wrapAppendix += currentOffset
					wrapOffset += currentOffset / 2;
				}

				purlin.scale.set((z+wrapAppendix) / this._purlinLength, this._scaleRatio, this._scaleRatio);
				purlin.position.set(wrapOffset - ((width / 2 - this._leanToConfig.back.cut_back.value) + (-width / 2 + this._leanToConfig.back.cut_front.value)) / 2, y, -length / 2 - zOffset - currentOffset);

				this._backSideLeantoPurlins.push(purlin);

				currentOffset += interval;
			}

			for (var i = 0; i < this._backSideLeantoPurlins.length; ++i) {
				this._container.add(this._backSideLeantoPurlins[i]);
			}
		}
	}

	private setBackSideLeantoEdge(width, height, length)
	{
		this._container.remove(this._backSideLeantoEdge);
		if (this._leanToConfig.back.enabled.value)
		{
			var bays = Main.getInstance().config.building.insetBay.back;

			var zOffset = 0;


			if (bays.count.value >= 1)
			{
				zOffset += bays.length_1.value;
			}
			if (bays.count.value >= 2)
			{
				zOffset += bays.length_2.value;
			}

			var h = this._leanToConfig.back.pitch.value*this._leanToConfig.back.length.value/12;
			var y = height - h - this._leanToConfig.back.drop.value - 0.35;
			var z = width - (this._leanToConfig.back.cut_back.value+this._leanToConfig.back.cut_front.value);
			var wrapAppendix = 0;
			var wrapOffset = 0;
			if (this._leanToConfig.wraparound_back_left.enabled.value)
			{
				wrapAppendix += this._leanToConfig.left.length.value;
				wrapOffset -= this._leanToConfig.left.length.value/2;
			}
			if (this._leanToConfig.wraparound_back_right.enabled.value)
			{
				wrapAppendix += this._leanToConfig.right.length.value;
				wrapOffset += this._leanToConfig.right.length.value/2;
			}
			this._backSideLeantoEdge.scale.set(this._scaleRatio, (z+wrapAppendix)/this._singleCeeHeight, this._scaleRatio);
			this._backSideLeantoEdge.position.set(wrapOffset - ((width/2-this._leanToConfig.back.cut_back.value) + (-width/2 + this._leanToConfig.back.cut_front.value))/2, y, -length/2 - this._leanToConfig.back.length.value - zOffset + 0.3);

			this._container.add(this._backSideLeantoEdge);
		}
	}

	private setBackSideLeantoWideFlanges(width, height, length)
	{
		this.eraseMeshArray(this._backSideLeantoWideFlanges);
		this.addBackSideLeantoWideFlanges(width, height, length);
	}
	private addBackSideLeantoWideFlanges(width, height, length)
	{
		if (this._leanToConfig.back.enabled.value)
		{
			var bays = Main.getInstance().config.building.insetBay.back;

			var zOffset = 0;


			if (bays.count.value >= 1)
			{
				zOffset += bays.length_1.value;
			}
			if (bays.count.value >= 2)
			{
				zOffset += bays.length_2.value;
			}

			var numberOfBays = Math.ceil((width - this._leanToConfig.back.cut_back.value - this._leanToConfig.back.cut_front.value) / PostManager.IntervalOnEnd);
			var requiredPosts = numberOfBays + 1;

			for (var i = 0; i < requiredPosts; ++i)
			{
				var x = -width/2+this._leanToConfig.back.cut_back.value + i / (requiredPosts-1) * (width-this._leanToConfig.back.cut_front.value-this._leanToConfig.back.cut_back.value);
				var h = this._leanToConfig.back.pitch.value*this._leanToConfig.back.length.value/24;
				var y = height - h - this._leanToConfig.back.drop.value - 1;
				var z = -length/2 - this._leanToConfig.back.length.value/2 - zOffset + 0.45;

				var fineTuneForEnds = 0;
				if (i == 0)
				{
					fineTuneForEnds = -0.4;
				}
				else if (i == requiredPosts - 1)
				{
					fineTuneForEnds = 0.4;
				}

				var wideFlange = this._wideFlange.clone();
				wideFlange.rotateOnAxis(new Vector3(1, 0, 0), 1.01*this.getAngleFromPitch(this._leanToConfig.back.pitch.value));
				wideFlange.scale.set(this._scaleRatio, Math.sqrt(this._leanToConfig.back.length.value*this._leanToConfig.back.length.value+h*h)/this._wideFlangeLength-0.15, this._scaleRatio);
				wideFlange.position.set(x-fineTuneForEnds, y, z);



				this._backSideLeantoWideFlanges.push(wideFlange);
			}

			for (var i = 0; i < this._backSideLeantoWideFlanges.length; ++i)
			{
				this._container.add(this._backSideLeantoWideFlanges[i]);
			}
		}
	}

	private setLeftSideLeantoPurlins(width, height, length, zPosOffset)
	{
		this.eraseMeshArray(this._leftSideLeantoPurlins);
		this.addLeftSideLeantoPurlins(width, height, length, zPosOffset);
	}

	private addLeftSideLeantoPurlins(width, height, length, zPosOffset)
	{
		if (this._leanToConfig.left.enabled.value)
		{
			var currentOffset = 1;
			var interval = 5;
			var z = length - (this._leanToConfig.left.cut_back.value + this._leanToConfig.left.cut_front.value);

			while (currentOffset < this._leanToConfig.left.length.value - 1) {
				var purlin = this._purlinLeft.clone();
				var h = this._leanToConfig.left.pitch.value * currentOffset / 12;
				var y = height - h - this._leanToConfig.left.drop.value - 0.35;

				var wrapAppendix = 0;
				var wrapOffset = 0;
				if (this._leanToConfig.wraparound_back_left.enabled.value)
				{
					wrapAppendix += currentOffset
					wrapOffset -= currentOffset / 2;
				}
				if (this._leanToConfig.wraparound_front_left.enabled.value)
				{
					wrapAppendix += currentOffset
					wrapOffset += currentOffset / 2;
				}

				purlin.scale.set((z+wrapAppendix) / this._purlinLength, this._scaleRatio, this._scaleRatio);
				purlin.position.set(-width / 2 - currentOffset, y, (wrapOffset - ((length / 2 - this._leanToConfig.left.cut_back.value) + (-length / 2 + this._leanToConfig.left.cut_front.value)) / 2) + zPosOffset);

				this._leftSideLeantoPurlins.push(purlin);

				currentOffset += interval;
			}

			for (var i = 0; i < this._leftSideLeantoPurlins.length; ++i) {
				this._container.add(this._leftSideLeantoPurlins[i]);
			}
		}
	}

	private setLeftSideLeantoWideFlanges(width, height, length, zPosOffset)
	{
		this.eraseMeshArray(this._leftSideLeantoWideFlanges);
		this.addLeftSideLeantoWideFlanges(width, height, length, zPosOffset);
	}


	private setLeftSideLeantoEdge(width, height, length, zPosOffset)
	{
		this._container.remove(this._leftSideLeantoEdge);
		if (this._leanToConfig.left.enabled.value)
		{

			var h = this._leanToConfig.left.pitch.value*this._leanToConfig.left.length.value/12;
			var y = height - h - this._leanToConfig.left.drop.value - 0.35;
			var z = length - (this._leanToConfig.left.cut_back.value+this._leanToConfig.left.cut_front.value);
			var wrapAppendix = 0;
			var wrapOffset = 0;
			if (this._leanToConfig.wraparound_back_left.enabled.value)
			{
				wrapAppendix += this._leanToConfig.back.length.value;
				wrapOffset -= this._leanToConfig.back.length.value/2;
			}
			if (this._leanToConfig.wraparound_front_left.enabled.value)
			{
				wrapAppendix += this._leanToConfig.front.length.value;
				wrapOffset += this._leanToConfig.front.length.value/2;
			}
			this._leftSideLeantoEdge.scale.set(this._scaleRatio, (z+wrapAppendix)/this._singleCeeHeight, this._scaleRatio);
			this._leftSideLeantoEdge.position.set(-width/2 - this._leanToConfig.left.length.value + 0.3, y, (wrapOffset - ((length/2-this._leanToConfig.left.cut_back.value) + (-length/2 + this._leanToConfig.left.cut_front.value))/2) + zPosOffset);

			this._container.add(this._leftSideLeantoEdge);
		}
	}

	private addLeftSideLeantoWideFlanges(width, height, length, zPosOffset)
	{
		if (this._leanToConfig.left.enabled.value)
		{
			var x = -width/2 - this._leanToConfig.left.length.value / 2 + 0.45;
			var h = this._leanToConfig.left.pitch.value*this._leanToConfig.left.length.value / 24;
			var y = height - h - this._leanToConfig.left.drop.value - 1;

			var finalLength = Math.sqrt(this._leanToConfig.left.length.value*this._leanToConfig.left.length.value+h*h)/this._wideFlangeLength-0.15;

			var numberOfBays = Math.ceil((length - this._leanToConfig.left.cut_back.value - this._leanToConfig.left.cut_front.value) / PostManager.IntervalOnSide);
			var requiredPosts = numberOfBays + 1;

			for (var i = 0; i < requiredPosts; ++i)
			{
				var z: number = -length/2+this._leanToConfig.left.cut_back.value + i / (requiredPosts-1) * (length-this._leanToConfig.left.cut_front.value-this._leanToConfig.left.cut_back.value);
				z += zPosOffset;

				var fineTuneForEnds = 0;
				if (i == 0)
				{
					fineTuneForEnds = -0.4;
				}
				else if (i == requiredPosts - 1)
				{
					fineTuneForEnds = 0.4;
				}

                var wideFlange = this._wideFlange.clone();
				wideFlange.rotateOnAxis(new Vector3(0, 1, 0), Math.PI/2);
				wideFlange.rotateOnAxis(new Vector3(1, 0, 0), 1.01*this.getAngleFromPitch(this._leanToConfig.left.pitch.value));
				wideFlange.scale.set(this._scaleRatio, finalLength, this._scaleRatio);
				wideFlange.position.set(x, y, z-fineTuneForEnds);

				this._leftSideLeantoWideFlanges.push(wideFlange);
			}




			var insetBays = Main.getInstance().config.building.insetBay;

			if (insetBays.front.count.value >= 1)
			{
				var wideFlange = this._wideFlange.clone();
				wideFlange.rotateOnAxis(new Vector3(0, 0, 1), Math.PI/2);
				wideFlange.rotateOnAxis(new Vector3(1, 0, 0), -1.01*this.getAngleFromPitch(this._leanToConfig.left.pitch.value));
				wideFlange.scale.set(this._scaleRatio, this._scaleRatio, finalLength);
				var z: number = length / 2 + insetBays.front.length_1.value - 0.5;
				wideFlange.position.set(x, y, z);

				this._leftSideLeantoWideFlanges.push(wideFlange);
			}
			if (insetBays.front.count.value >= 2)
			{
				var wideFlange = this._wideFlange.clone();
				wideFlange.rotateOnAxis(new Vector3(0, 0, 1), Math.PI/2);
				wideFlange.rotateOnAxis(new Vector3(1, 0, 0), -1.01*this.getAngleFromPitch(this._leanToConfig.left.pitch.value));
				wideFlange.scale.set(this._scaleRatio, this._scaleRatio, finalLength);
				var z: number = length / 2 + insetBays.front.length_1.value + insetBays.front.length_2.value - 0.5;
				wideFlange.position.set(x, y, z);

				this._leftSideLeantoWideFlanges.push(wideFlange);
			}

			if (insetBays.back.count.value >= 1)
			{
				var wideFlange = this._wideFlange.clone();
				wideFlange.rotateOnAxis(new Vector3(0, 0, 1), Math.PI/2);
				wideFlange.rotateOnAxis(new Vector3(1, 0, 0), -1.01*this.getAngleFromPitch(this._leanToConfig.left.pitch.value));
				wideFlange.scale.set(this._scaleRatio, this._scaleRatio, finalLength);
				var z: number = - length / 2 - insetBays.back.length_1.value + 0.5;
				wideFlange.position.set(x, y, z);

				this._leftSideLeantoWideFlanges.push(wideFlange);
			}
			if (insetBays.back.count.value >= 2)
			{
				var wideFlange = this._wideFlange.clone();
				wideFlange.rotateOnAxis(new Vector3(0, 0, 1), Math.PI/2);
				wideFlange.rotateOnAxis(new Vector3(1, 0, 0), -1.01*this.getAngleFromPitch(this._leanToConfig.left.pitch.value));
				wideFlange.scale.set(this._scaleRatio, this._scaleRatio, finalLength);
				var z: number = - length / 2 - insetBays.back.length_1.value - insetBays.back.length_2.value + 0.5;
				wideFlange.position.set(x, y, z);

				this._leftSideLeantoWideFlanges.push(wideFlange);
			}




			for (var i = 0; i < this._leftSideLeantoWideFlanges.length; ++i)
			{
				this._container.add(this._leftSideLeantoWideFlanges[i]);
			}
		}
	}


	private setRightSideLeantoPurlins(width, height, length, zPosOffset)
	{
		this.eraseMeshArray(this._rightSideLeantoPurlins);
		this.addRightSideLeantoPurlins(width, height, length, zPosOffset);
	}

	private addRightSideLeantoPurlins(width, height, length, zPosOffset)
	{
		if (this._leanToConfig.right.enabled.value)
		{
			var currentOffset = 1;
			var interval = 5;
			var z = length - (this._leanToConfig.right.cut_back.value + this._leanToConfig.right.cut_front.value);

			while (currentOffset < this._leanToConfig.right.length.value - 1) {
				var purlin = this._purlinRight.clone();
				var h = this._leanToConfig.right.pitch.value * currentOffset / 12;
				var y = height - h - this._leanToConfig.right.drop.value - 0.35;

				var wrapAppendix = 0;
				var wrapOffset = 0;
				if (this._leanToConfig.wraparound_back_right.enabled.value)
				{
					wrapAppendix += currentOffset
					wrapOffset -= currentOffset / 2;
				}
				if (this._leanToConfig.wraparound_front_right.enabled.value)
				{
					wrapAppendix += currentOffset
					wrapOffset += currentOffset / 2;
				}

				purlin.scale.set((z+wrapAppendix) / this._purlinLength, this._scaleRatio, this._scaleRatio);
				purlin.position.set(width / 2 + currentOffset, y, (wrapOffset - ((length / 2 - this._leanToConfig.right.cut_back.value) + (-length / 2 + this._leanToConfig.right.cut_front.value)) / 2) + zPosOffset);

				this._rightSideLeantoPurlins.push(purlin);

				currentOffset += interval;
			}

			for (var i = 0; i < this._rightSideLeantoPurlins.length; ++i) {
				this._container.add(this._rightSideLeantoPurlins[i]);
			}
		}
	}

	private setRightSideLeantoWideFlanges(width, height, length, zPosOffset)
	{
		this.eraseMeshArray(this._rightSideLeantoWideFlanges);
		this.addRightSideLeantoWideFlanges(width, height, length, zPosOffset);
	}

	private setRightSideLeantoEdge(width, height, length, zPosOffset)
	{
		this._container.remove(this._rightSideLeantoEdge);
		if (this._leanToConfig.right.enabled.value)
		{

			var h = this._leanToConfig.right.pitch.value*this._leanToConfig.right.length.value/12;
			var y = height - h - this._leanToConfig.right.drop.value - 0.35;
			var z = length - (this._leanToConfig.right.cut_back.value+this._leanToConfig.right.cut_front.value);
			var wrapAppendix = 0;
			var wrapOffset = 0;
			if (this._leanToConfig.wraparound_back_right.enabled.value)
			{
				wrapAppendix += this._leanToConfig.back.length.value;
				wrapOffset -= this._leanToConfig.back.length.value/2;
			}
			if (this._leanToConfig.wraparound_front_right.enabled.value)
			{
				wrapAppendix += this._leanToConfig.front.length.value;
				wrapOffset += this._leanToConfig.front.length.value/2;
			}
			this._rightSideLeantoEdge.scale.set(this._scaleRatio, (z+wrapAppendix)/this._singleCeeHeight, this._scaleRatio);
			this._rightSideLeantoEdge.position.set(width/2 + this._leanToConfig.right.length.value - 0.3, y, (wrapOffset - ((length/2-this._leanToConfig.right.cut_back.value) + (-length/2 + this._leanToConfig.right.cut_front.value))/2) + zPosOffset);

			this._container.add(this._rightSideLeantoEdge);
		}
	}

	private addRightSideLeantoWideFlanges(width, height, length, zPosOffset)
	{
		if (this._leanToConfig.right.enabled.value)
		{
			var numberOfBays = Math.ceil((length - this._leanToConfig.right.cut_back.value - this._leanToConfig.right.cut_front.value) / PostManager.IntervalOnSide);
			var requiredPosts = numberOfBays + 1;

			var x = width/2 + this._leanToConfig.right.length.value/2 - 0.45;
			var h = this._leanToConfig.right.pitch.value*this._leanToConfig.right.length.value/24;
			var y = height - h - this._leanToConfig.right.drop.value - 1;

			var finalLength = Math.sqrt(this._leanToConfig.right.length.value*this._leanToConfig.right.length.value+h*h)/this._wideFlangeLength-0.15;

			for (var i = 0; i < requiredPosts; ++i)
			{
				var z = -length/2+this._leanToConfig.right.cut_back.value + i / (requiredPosts-1) * (length-this._leanToConfig.right.cut_front.value-this._leanToConfig.right.cut_back.value);
				z += zPosOffset;

				var fineTuneForEnds = 0;
				if (i == 0)
				{
					fineTuneForEnds = -0.4;
				}
				else if (i == requiredPosts - 1)
				{
					fineTuneForEnds = 0.4;
				}

				var wideFlange = this._wideFlange.clone();
				wideFlange.rotateOnAxis(new Vector3(0, 1, 0), -Math.PI/2);
				wideFlange.rotateOnAxis(new Vector3(1, 0, 0), 1.01*this.getAngleFromPitch(this._leanToConfig.right.pitch.value));
				wideFlange.scale.set(this._scaleRatio, finalLength, this._scaleRatio);
				wideFlange.position.set(x, y, z-fineTuneForEnds);

				this._rightSideLeantoWideFlanges.push(wideFlange);
			}

			var insetBays = Main.getInstance().config.building.insetBay;

			if (insetBays.front.count.value >= 1)
			{
				var wideFlange = this._wideFlange.clone();
				wideFlange.rotateOnAxis(new Vector3(0, 0, 1), -Math.PI/2);
				wideFlange.rotateOnAxis(new Vector3(1, 0, 0), -1.01*this.getAngleFromPitch(this._leanToConfig.right.pitch.value));
				wideFlange.scale.set(this._scaleRatio, this._scaleRatio, finalLength);
				var z = length / 2 + insetBays.front.length_1.value - 0.5;
				wideFlange.position.set(x, y, z);
				this._rightSideLeantoWideFlanges.push(wideFlange);
			}
			if (insetBays.front.count.value >= 2)
			{
				var wideFlange = this._wideFlange.clone();
				wideFlange.rotateOnAxis(new Vector3(0, 0, 1), -Math.PI/2);
				wideFlange.rotateOnAxis(new Vector3(1, 0, 0), -1.01*this.getAngleFromPitch(this._leanToConfig.right.pitch.value));
				wideFlange.scale.set(this._scaleRatio, this._scaleRatio, finalLength);
				var z = length / 2 + insetBays.front.length_1.value + insetBays.front.length_2.value - 0.5;
				wideFlange.position.set(x, y, z);
				this._rightSideLeantoWideFlanges.push(wideFlange);
			}

			if (insetBays.back.count.value >= 1)
			{
				var wideFlange = this._wideFlange.clone();
				wideFlange.rotateOnAxis(new Vector3(0, 0, 1), -Math.PI/2);
				wideFlange.rotateOnAxis(new Vector3(1, 0, 0), -1.01*this.getAngleFromPitch(this._leanToConfig.right.pitch.value));
				wideFlange.scale.set(this._scaleRatio, this._scaleRatio, finalLength);
				var z = - length / 2 - insetBays.back.length_1.value + 0.5;
				wideFlange.position.set(x, y, z);
				this._rightSideLeantoWideFlanges.push(wideFlange);
			}
			if (insetBays.back.count.value >= 2)
			{
				var wideFlange = this._wideFlange.clone();
				wideFlange.rotateOnAxis(new Vector3(0, 0, 1), -Math.PI/2);
				wideFlange.rotateOnAxis(new Vector3(1, 0, 0), -1.01*this.getAngleFromPitch(this._leanToConfig.right.pitch.value));
				wideFlange.scale.set(this._scaleRatio, this._scaleRatio, finalLength);
				var z = - length / 2 - insetBays.back.length_1.value - insetBays.back.length_2.value + 0.5;
				wideFlange.position.set(x, y, z);
				this._rightSideLeantoWideFlanges.push(wideFlange);
			}

			for (var i = 0; i < this._rightSideLeantoWideFlanges.length; ++i)
			{
				this._container.add(this._rightSideLeantoWideFlanges[i]);
			}
		}
	}

	private setRollupBeams(width: number, height: number, length: number, pitch: number)
	{
		this.eraseMeshArray(this._rollupBeams);
		this.addRollupBeams(width, height, length, pitch);
	}
	private addRollupBeams(width: number, height: number, length: number, pitch: number)
	{
		var fineTuneForLength = 0.35;
		var fineTuneForRollups = 0.12;


		//
		// Front Wall
		//
		var whichWall = Building.WALLS.FRONT;
		var addonsCount = this._building.addons[whichWall].length;

		for (var i = 0; i < addonsCount; ++i)
		{
			var rollup = this._building.addons[whichWall][i];
			if (rollup instanceof WallAddon)
			{
				if (rollup.itemId.includes("rollup"))
				{
					var xLeft  = rollup.getPosition().horizontal + rollup.width/2 + fineTuneForRollups;
					var xRight = rollup.getPosition().horizontal - rollup.width/2 - fineTuneForRollups;
					var hLeft = (height + this._building.config.metrics.tan*(width/2 - Math.abs(xLeft)))-1.2;
					var hRight = (height + this._building.config.metrics.tan*(width/2 - Math.abs(xRight)))-1.2;

					if (this._config.building.single_slope.value)
					{
						// if the wall is back, from the inside the right column is the higher one and vica versa
						hLeft  = height + Math.abs(xLeft + width/2) * pitch / 12 - 1.2;
						hRight = height + Math.abs(width/2 + xRight)* pitch / 12 - 1.2;
					}

					var rollupLeft = this._singleCeeLeftSide.clone();
					rollupLeft.position.set(xLeft, hLeft/2, length/2 - fineTuneForLength);
					rollupLeft.scale.set(this._scaleRatio, hLeft/this._singleCeeHeight, this._scaleRatio);
					this._rollupBeams.push(rollupLeft);

					var rollupRight = this._singleCeeRightSide.clone();
					rollupRight.position.set(xRight, hRight/2, length/2 - fineTuneForLength);
					rollupRight.scale.set(this._scaleRatio, hRight/this._singleCeeHeight, this._scaleRatio);
					this._rollupBeams.push(rollupRight);

					var rollupAbove = this._singleCeeLeftSide.clone();
					rollupAbove.rotateOnAxis(new Vector3(1, 0, 0), -Math.PI/2);
					rollupAbove.position.set(rollup.getPosition().horizontal, rollup.height+0.1, length/2 - fineTuneForLength);
					rollupAbove.scale.set(this._scaleRatio, rollup.width / this._singleCeeHeight, this._scaleRatio);
					this._rollupBeams.push(rollupAbove);

					rollupLeft.userData.wall = whichWall;
					rollupRight.userData.wall = whichWall;
					rollupAbove.userData.wall = whichWall;
				}
			}
		}

		//
		// Back Wall
		//
		whichWall = Building.WALLS.BACK;
		addonsCount = this._building.addons[whichWall].length;

		for (var i = 0; i < addonsCount; ++i)
		{
			var rollup = this._building.addons[whichWall][i];
			if (rollup instanceof WallAddon)
			{
				if (rollup.itemId.includes("rollup"))
				{
					var xLeft  = rollup.getPosition().horizontal + rollup.width/2 + fineTuneForRollups;
					var xRight = rollup.getPosition().horizontal - rollup.width/2 - fineTuneForRollups;
					var hLeft = (height + this._building.config.metrics.tan*(width/2 - Math.abs(xLeft)))-1.2;
					var hRight = (height + this._building.config.metrics.tan*(width/2 - Math.abs(xRight)))-1.2;

					if (this._config.building.single_slope.value)
					{
						// if the wall is back, the right column is the higher one and vica versa
						hLeft  = height + Math.abs(xLeft + width/2) * pitch / 12 - 1.2;
						hRight = height + Math.abs(width/2 + xRight)* pitch / 12 - 1.2;
					}

					var rollupLeft = this._singleCeeLeftSide.clone();
					rollupLeft.position.set(xLeft, hLeft/2, -length/2 + fineTuneForLength);
					rollupLeft.scale.set(this._scaleRatio, hLeft/this._singleCeeHeight, this._scaleRatio);
					this._rollupBeams.push(rollupLeft);

					var rollupRight = this._singleCeeRightSide.clone();
					rollupRight.position.set(xRight, hRight/2, -length/2 + fineTuneForLength);
					rollupRight.scale.set(this._scaleRatio, hRight/this._singleCeeHeight, this._scaleRatio);
					this._rollupBeams.push(rollupRight);

					var rollupAbove = this._singleCeeLeftSide.clone();
					rollupAbove.rotateOnAxis(new Vector3(1, 0, 0), -Math.PI/2);
					rollupAbove.position.set(rollup.getPosition().horizontal, rollup.height+0.1, -length/2 + fineTuneForLength);
					rollupAbove.scale.set(this._scaleRatio, rollup.width/this._singleCeeHeight, this._scaleRatio);
					this._rollupBeams.push(rollupAbove);

					rollupLeft.userData.wall = whichWall;
					rollupRight.userData.wall = whichWall;
					rollupAbove.userData.wall = whichWall;
				}
			}
		}

		//
		// Right Wall
		//
		whichWall = Building.WALLS.RIGHT;
		addonsCount = this._building.addons[whichWall].length;

		for (var i = 0; i < addonsCount; ++i)
		{
			var rollup = this._building.addons[whichWall][i];
			if (rollup instanceof WallAddon)
			{
				if (rollup.itemId.includes("rollup"))
				{
					var xLeft  = rollup.getPosition().horizontal + rollup.width/2 + fineTuneForRollups;
					var xRight = rollup.getPosition().horizontal - rollup.width/2 - fineTuneForRollups;

					var hFineTune = height - 0.2;
					var singleSlopeOffset = 0;
					if (this._config.building.single_slope.value)
					{
						singleSlopeOffset = width*pitch/12 - 0.3;
					}
					hFineTune += singleSlopeOffset;

					var rollupLeft = this._singleCeeBackSide.clone();
					rollupLeft.position.set(width/2 - fineTuneForLength, hFineTune/2, xLeft);
					rollupLeft.scale.set(this._scaleRatio, hFineTune/this._singleCeeHeight, this._scaleRatio);
					this._rollupBeams.push(rollupLeft);

					var rollupRight = this._singleCeeFrontSide.clone();
					rollupRight.position.set(width/2 - fineTuneForLength, hFineTune/2, xRight);
					rollupRight.scale.set(this._scaleRatio, hFineTune/this._singleCeeHeight, this._scaleRatio);
					this._rollupBeams.push(rollupRight);

					var rollupAbove = this._singleCeeFrontSide.clone();
					rollupAbove.rotateOnAxis(new Vector3(1, 0, 0), -Math.PI/2);
					rollupAbove.position.set(width/2 - fineTuneForLength, rollup.height+0.1, rollup.getPosition().horizontal);
					rollupAbove.scale.set(this._scaleRatio, rollup.width/this._singleCeeHeight, this._scaleRatio);
					this._rollupBeams.push(rollupAbove);

					rollupLeft.userData.wall = whichWall;
					rollupRight.userData.wall = whichWall;
					rollupAbove.userData.wall = whichWall;
				}
			}
		}

		//
		// Left Wall
		//
		whichWall = Building.WALLS.LEFT;
		addonsCount = this._building.addons[whichWall].length;

		for (var i = 0; i < addonsCount; ++i)
		{
			var rollup = this._building.addons[whichWall][i];
			if (rollup instanceof WallAddon)
			{
				if (rollup.itemId.includes("rollup"))
				{
					var xLeft  = rollup.getPosition().horizontal + rollup.width/2 + fineTuneForRollups;
					var xRight = rollup.getPosition().horizontal - rollup.width/2 - fineTuneForRollups;

					var hFineTune = height - 0.2;

					var rollupLeft = this._singleCeeBackSide.clone();
					rollupLeft.position.set(-width/2 + fineTuneForLength, hFineTune/2, xLeft);
					rollupLeft.scale.set(this._scaleRatio, hFineTune/this._singleCeeHeight, this._scaleRatio);
					this._rollupBeams.push(rollupLeft);

					var rollupRight = this._singleCeeFrontSide.clone();
					rollupRight.position.set(-width/2 + fineTuneForLength, hFineTune/2, xRight);
					rollupRight.scale.set(this._scaleRatio, hFineTune/this._singleCeeHeight, this._scaleRatio);
					this._rollupBeams.push(rollupRight);

					var rollupAbove = this._singleCeeFrontSide.clone();
					rollupAbove.rotateOnAxis(new Vector3(1, 0, 0), -Math.PI/2);
					rollupAbove.position.set(-width/2 + fineTuneForLength, rollup.height+0.1, rollup.getPosition().horizontal);
					rollupAbove.scale.set(this._scaleRatio, rollup.width/this._singleCeeHeight, this._scaleRatio);
					this._rollupBeams.push(rollupAbove);

					rollupLeft.userData.wall = whichWall;
					rollupRight.userData.wall = whichWall;
					rollupAbove.userData.wall = whichWall;
				}
			}
		}

		for (var i = 0; i < this._rollupBeams.length; ++i)
		{
			this._container.add(this._rollupBeams[i]);
		}
	}
	private setPurlins(width, height, length, pitch)
	{
		this.eraseMeshArray(this._purlins);
		this.addPurlins(width, height, length, pitch);
	}
	private addPurlins(width, height, length, pitch)
	{
		var purlinInterval = 5; //feet
		var fineTune = 0.45;
		var currentOffset = 1;
		var top = this._config.metrics.top;

		while (currentOffset < width/2-2)
		{
			var hLeft = top - Math.cos(this.getAngleFromPitch(pitch))*currentOffset - fineTune;
			var hRight = hLeft;
			if (this._config.building.single_slope.value)
			{
				hRight = top + Math.cos(this.getAngleFromPitch(pitch))*currentOffset - fineTune;
			}
			var purlinLeft = this._purlinLeft.clone();
			//			   x   z   y
			purlinLeft.scale.set(length/this._purlinLength, this._scaleRatio, this._scaleRatio);
			purlinLeft.position.set(-currentOffset, hLeft, 0);
			this._purlins.push(purlinLeft);

			var purlinRight = this._purlinRight.clone();
			//			   x   z   y
			purlinRight.scale.set(length/this._purlinLength, this._scaleRatio, this._scaleRatio);
			purlinRight.position.set(currentOffset, hRight, 0);
			this._purlins.push(purlinRight);
			
			
			currentOffset += purlinInterval;
		}

		for (var i = 0; i < this._purlins.length; ++i)
		{
			this._container.add(this._purlins[i]);
		}
	}
	private getAngleFromPitch(pitch?: number)
	{
		if (pitch != null)
		{
			return (Math.PI/2)-Math.asin(pitch/12);
		}

		return Math.PI/2 - this._config.metrics.alpha;
	}
	private setSingleCees(width, height, length, pitch)
	{
		this.setVerticalCornerCees(width, height, length, pitch);
		this.setCornerPitchCees(width, height, length, pitch, 4);
		this.setSideCees(width, height, length, pitch);
	}
	private setWideFlanges(width, height, length, pitch)
	{
		this.eraseMeshArray(this._wideFlangesLeftSide);
		this.eraseMeshArray(this._wideFlangesRightSide);
		this.addWideFlanges(width, height, length, pitch);
	}
	private addWideFlanges(width, height, length, pitch)
	{
		var fineTune = 1.1;
		var numberOfBays = Math.ceil(length / PostManager.IntervalOnSide);
		var requiredPosts = numberOfBays + 1; // numberofbays + 1 - 2 (the columns for 2 endpoints are single cees, and are constructed elsewhere)

		var angleForLeft = this.getAngleFromPitch();
		var angleForRight = angleForLeft;
		var singleSlopeOffsetX = 0;
		var singleSlopeOffsetY = 0;
		if (this._config.building.single_slope.value)
		{
			angleForRight *= -1;
			singleSlopeOffsetX += width/50 - 0.1;
			singleSlopeOffsetY += (width*pitch) / 24;
		}

		var plusHeight = (width / 2)*(pitch/12);
		var roofWidth = Math.sqrt(plusHeight*plusHeight + (width / 2)*(width / 2)) - 0.1; // half of the roof
		for (var i = 1; i < requiredPosts-1; ++i)
		{
			var leftSideWideFlange = this._wideFlange.clone();
			leftSideWideFlange.rotateOnAxis(new Vector3(0, 1, 0), Math.PI/2);
			leftSideWideFlange.rotateOnAxis(new Vector3(1, 0, 0), angleForLeft);

			var rightSideWideFlange = this._wideFlange.clone();
			rightSideWideFlange.rotateOnAxis(new Vector3(0, 1, 0), -Math.PI/2);
			rightSideWideFlange.rotateOnAxis(new Vector3(1, 0, 0), angleForRight);

			var x = -width/4 + width / 105;
			var y = height+(width*pitch/48) - fineTune;
			var z = -length/2 + i / (requiredPosts-1) * length;

			var wideFlangeFinalLength = roofWidth / this._wideFlangeLength;

			leftSideWideFlange.position.set(x, y, z);
			leftSideWideFlange.scale.set(this._scaleRatio, wideFlangeFinalLength, this._scaleRatio);

			x = -x + singleSlopeOffsetX;
			y += singleSlopeOffsetY;
			rightSideWideFlange.position.set(x, y, z);
			rightSideWideFlange.scale.set(this._scaleRatio, wideFlangeFinalLength - singleSlopeOffsetX/25,  this._scaleRatio);

			this._wideFlangesLeftSide.push(leftSideWideFlange);
			this._wideFlangesRightSide.push(rightSideWideFlange);
		}

		for (var i = 0; i < this._wideFlangesLeftSide.length; ++i)
		{
			this._container.add(this._wideFlangesLeftSide[i]);
		}
		for (var i = 0; i < this._wideFlangesRightSide.length; ++i)
		{
			this._container.add(this._wideFlangesRightSide[i]);
		}
	}
	private setSideCees(width, height, length, pitch)
	{
		this.eraseMeshArray(this._singleCeesBackSide);
		this.eraseMeshArray(this._singleCeesFrontSide);
		this.eraseMeshArray(this._singleCeesRightSide);
		this.eraseMeshArray(this._singleCeesLeftSide);
		this.addSideCees(width, height, length, pitch);
	}
	private addSideCees(width, height, length, pitch)
	{
		var fineTune = 0.8;
		//
		// left and right
		//

		var numberOfBays = Math.ceil(length / PostManager.IntervalOnSide);
		var requiredPosts = numberOfBays + 1; // numberofbays + 1 - 2 (the columns for 2 endpoints are constructed elsewhere)

		var rightHeight = height;
		if (this._config.building.single_slope.value)
		{
			rightHeight += (width*pitch)/12;
		}

		for (var i = 1; i < requiredPosts-1; ++i)
		{
			var leftSideColumn  = this._singleCeeLeftSide.clone();
			var rightSideColumn = this._singleCeeRightSide.clone();

			var x = -width/2 + 0.12;
			var y =  height/2 - fineTune;
			var yRight =  rightHeight/2 - fineTune;
			var z = -length/2 + i / (requiredPosts-1) * length;

			leftSideColumn.position.set(x, y, z);
			leftSideColumn.scale.set(this._scaleRatio, (height/this._singleCeeHeight)-fineTune/2, this._scaleRatio);

			x = -x;

			rightSideColumn.position.set(x, yRight, z);
			rightSideColumn.scale.set(this._scaleRatio, (rightHeight/this._singleCeeHeight)-fineTune/2, this._scaleRatio);


			this._singleCeesLeftSide.push(leftSideColumn);
			this._singleCeesRightSide.push(rightSideColumn);
		}

		for (var i = 0; i < this._singleCeesLeftSide.length; ++i)
		{
			this._container.add(this._singleCeesLeftSide[i]);
		}
		for (var i = 0; i < this._singleCeesRightSide.length; ++i)
		{
			this._container.add(this._singleCeesRightSide[i]);
		}

		//
		// front and back side
		//
		numberOfBays = Math.ceil(width / PostManager.IntervalOnEnd);
		requiredPosts = numberOfBays + 1; // numberofbays + 1 - 2 (the columns for 2 endpoints are constructed elsewhere)

		for (var i = 1; i < requiredPosts-1; ++i)
		{
			var frontSideColumn  = this._singleCeeFrontSide.clone();
			var backSideColumn = this._singleCeeBackSide.clone();

			var x = -width/2 + i / (requiredPosts-1) * width;
			var b = x >= 0 ? Math.abs(x-width/2) : Math.abs(-x-width/2);
			var h = height + this._building.config.metrics.tan*b - fineTune;
			var z =  length/2-0.12;

			if (Main.getInstance().config.building.single_slope.value)
			{
				if (x > 0)
				{
					h = height + b*pitch / 6 - fineTune;
				}
			}
			var y =  h/2;
			//
			// Front
			//
			frontSideColumn.position.set(x, y, z);
			frontSideColumn.scale.set(this._scaleRatio, (h/this._singleCeeHeight), this._scaleRatio);

			var whichWall = Building.WALLS.FRONT;
			var addonsCount = this._building.addons[whichWall].length;

			var isItValid = true;

			for (var j = 0; j < addonsCount; ++j)
			{
				var rollup = this._building.addons[whichWall][j];
				if (rollup instanceof WallAddon)
				{
					if (rollup.itemId.substring(0, 6) === "rollup")
					{
						if (rollup.getPosition().horizontal - rollup.width/2 < x + 2 && rollup.getPosition().horizontal + rollup.width / 2 > x - 2)
						{
							isItValid = false;
							break;
						}
					}
				}
			}

			if (isItValid)
			{
				this._singleCeesFrontSide.push(frontSideColumn);
			}


			//
			// Back
			//
			z = -z;

			backSideColumn.position.set(x, y, z);
			backSideColumn.scale.set(this._scaleRatio, (h/this._singleCeeHeight), this._scaleRatio);

			isItValid = true;

			whichWall = Building.WALLS.BACK;
			addonsCount = this._building.addons[whichWall].length;

			for (var j = 0; j < addonsCount; ++j)
			{
				var rollup = this._building.addons[whichWall][j];
				if (rollup instanceof WallAddon)
				{

					if (this._building.addons)
					{
						if (rollup.itemId.substring(0, 6) === "rollup")
						{
							if (rollup.getPosition().horizontal - rollup.width/2 < x + 2 && rollup.getPosition().horizontal + rollup.width / 2 > x - 2)
							{
								isItValid = false;
								break;
							}
						}
					}
				}
			}

			if (isItValid)
			{
				this._singleCeesBackSide.push(backSideColumn);
			}
		}

		for (var i = 0; i < this._singleCeesFrontSide.length; ++i)
		{
			this._container.add(this._singleCeesFrontSide[i]);
		}
		for (var i = 0; i < this._singleCeesBackSide.length; ++i)
		{
			this._container.add(this._singleCeesBackSide[i]);
		}
	}

	public get beams()
	{
		return {
			singleCees: {
				front: this._singleCeesFrontSide,
				back: this._singleCeesBackSide,
				left: this._singleCeesLeftSide,
				right: this._singleCeesRightSide
			},
			rollupBeams: this._rollupBeams
		}
	}

	private setCornerPitchCees(width, height, length, pitch, ind)
	{
		var fineTuneHeight = 1.1;
		var fineTuneX = 0.1;
		let angle = -this.getAngleFromPitch();
		this._container.remove(this._singleCees[ind]); // top left pitch
		const singleCeeHeight = (Math.sqrt(width*width+(pitch/12)*width*(pitch/12)*width)/this._singleCeeHeight)/2-0.1;
		this._singleCees[ind] = this._singleCee.clone();
		this._singleCees[ind].rotateOnAxis(new Vector3(0, 0, 1), Math.PI);
		this._singleCees[ind].rotateOnAxis(new Vector3(0, 0, 1), angle);
		this._singleCees[ind].position.set(-width/4+fineTuneX, height+(width*pitch/48)-fineTuneHeight, -length/2+0.3);
		this._singleCees[ind].scale.set(this._scaleRatio, singleCeeHeight, this._scaleRatio);
		this._container.add(this._singleCees[ind]);

		this._container.remove(this._singleCees[ind+1]); // bottom left pitch
		this._singleCees[ind+1] = this._singleCee.clone();
		this._singleCees[ind+1].rotateOnAxis(new Vector3(0, 0, 1), Math.PI);
		this._singleCees[ind+1].rotateOnAxis(new Vector3(0, 0, 1), angle);
		this._singleCees[ind+1].position.set(-width/4+fineTuneX, height+(width*pitch/48)-fineTuneHeight, length/2-0.3);
		this._singleCees[ind+1].scale.set(this._scaleRatio, singleCeeHeight, this._scaleRatio);
		this._container.add(this._singleCees[ind+1]);


		height += (width*pitch/48) - fineTuneHeight;
		if (this._config.building.single_slope.value)
		{
			angle *= -1;
			height += width*pitch / 24;
		}
		this._container.remove(this._singleCees[ind+2]); // bottom right pitch
		this._singleCees[ind+2] = this._singleCee.clone();
		this._singleCees[ind+2].rotateOnAxis(new Vector3(0, 0, 1), -angle);
		this._singleCees[ind+2].position.set(width/4-fineTuneX, height, length/2-0.3);
		this._singleCees[ind+2].scale.set(this._scaleRatio, singleCeeHeight, this._scaleRatio);
		this._container.add(this._singleCees[ind+2]);

		this._container.remove(this._singleCees[ind+3]); // top right pitch
		this._singleCees[ind+3] = this._singleCee.clone();
		this._singleCees[ind+3].rotateOnAxis(new Vector3(0, 0, 1), Math.PI);
		this._singleCees[ind+3].rotateOnAxis(new Vector3(0, 0, 1), -angle);
		this._singleCees[ind+3].position.set(width/4-fineTuneX, height, -length/2+0.3);
		this._singleCees[ind+3].scale.set(this._scaleRatio, singleCeeHeight, this._scaleRatio);
		this._container.add(this._singleCees[ind+3]);

	}
	private setVerticalCornerCees(width, height, length, pitch)
	{
		var fineTune = 0.75;

		//0 top left
		//1 bottom left
		//2 bottom right
		//3 top right

		this._singleCees[0].scale.set(this._scaleRatio, (height/this._singleCeeHeight)-fineTune/2, this._scaleRatio);
		this._singleCees[0].position.set(-width/2+0.35, height/2-fineTune, -length/2+0.1);

		this._singleCees[1].scale.set(this._scaleRatio, (height/this._singleCeeHeight)-fineTune/2, this._scaleRatio);
		this._singleCees[1].position.set(-width/2+0.35, height/2-fineTune, length/2-0.1);

		if (this._config.building.single_slope.value)
		{
			height += (width*pitch / 12);
		}
		this._singleCees[2].scale.set(this._scaleRatio, (height/this._singleCeeHeight)-fineTune/2, this._scaleRatio);
		this._singleCees[2].position.set(width/2-0.35, height/2-fineTune, length/2-0.1);

		this._singleCees[3].scale.set(this._scaleRatio, (height/this._singleCeeHeight)-fineTune/2, this._scaleRatio);
		this._singleCees[3].position.set(width/2-0.35, height/2-fineTune, -length/2+0.1);

	}
	private setEaveStruts(width, height, length, pitch)
	{
		var eave_strut_length = 6.083; //meter
		this._eave_strut_left.position.set(-width/2 + 0.3, height - 0.38, 0.0);
		this._eave_strut_left.scale.set((length / eave_strut_length), this._scaleRatio, this._scaleRatio);
		if (this._config.building.single_slope.value)
		{
			this._eave_strut_right.position.set(width/2 - 0.3, height + (width*pitch / 12) - 0.58, 0.0);
		}
		else
		{
			this._eave_strut_right.position.set(width/2 - 0.3, height - 0.38, 0.0);
		}
		this._eave_strut_right.scale.set((length / eave_strut_length), this._scaleRatio, this._scaleRatio);
	}
	private setGirts(width, height, length, pitch)
	{
		this.eraseMeshArray(this._girts);
		this.addGirts(width, height, length, pitch);
	}
	private addGirts(width, height, length, pitch)
	{
		var girt_length = 2.624; //meter
		var girt_interval = 3.667; //feet

		var intervals = Math.floor((height-2)/girt_interval);

		for (var i = 0; i < intervals; ++i)
		{
			const girtRight = this._girtRight.clone();
			girtRight.position.set(width/2 - 0.15, (i+1)*girt_interval, 0);
			girtRight.scale.set(this._scaleRatio, this._scaleRatio*0.5, (length-0.7)/girt_length);
			this._girts.push(THREEUtils.subtractNodesFromMeshWithClean(girtRight, this._building.addons[Building.WALLS.RIGHT]));

			const girtLeft = this._girtLeft.clone();
			girtLeft.position.set(-width/2 + 0.15, (i+1)*girt_interval, 0);
			girtLeft.scale.set(this._scaleRatio, this._scaleRatio*0.5, (length-0.7)/girt_length);
			this._girts.push(THREEUtils.subtractNodesFromMeshWithClean(girtLeft, this._building.addons[Building.WALLS.LEFT]));

			const girtFront = this._girtFront.clone();
			girtFront.position.set(0, (i+1)*girt_interval,  length/2 - 0.15);
			girtFront.scale.set(this._scaleRatio, this._scaleRatio*0.5, (width-0.7)/girt_length);
			this._girts.push(THREEUtils.subtractNodesFromMeshWithClean(girtFront, this._building.addons[Building.WALLS.FRONT]));

			const girtBack = this._girtBack.clone();
			girtBack.position.set(0, (i+1)*girt_interval, -length/2 + 0.15);
			girtBack.scale.set(this._scaleRatio, this._scaleRatio*0.5, (width-0.7)/girt_length);
			this._girts.push(THREEUtils.subtractNodesFromMeshWithClean(girtBack, this._building.addons[Building.WALLS.BACK]));
		}
		if (this._config.building.single_slope.value)
		{
			var slopeIntervals = Math.floor((height + width*pitch/12 - 2) / girt_interval) - intervals;
			for (var i = intervals; i < intervals + slopeIntervals; ++i)
			{
				const girtRight = this._girtRight.clone();
				girtRight.position.set(width/2 - 0.15, (i+1)*girt_interval, 0);
				girtRight.scale.set(this._scaleRatio, this._scaleRatio*0.5, (length-0.7)/girt_length);
				this._girts.push(THREEUtils.subtractNodesFromMeshWithClean(girtRight, this._building.addons[Building.WALLS.RIGHT]));
			}
		}


		for (var i = 0; i < this._girts.length; ++i)
		{
			this._container.add(this._girts[i]);
		}
	}

	private addListeners = () =>
	{
		this._config.building.length.signal.add(this.onUpdate);

		this._config.building.height.signal.add(this.onUpdate);
		this._config.building.pitch.signal.add(this.onUpdate);
		this._config.building.width.signal.add(this.onUpdate);

		this._config.building.single_slope.signal.add(this.onUpdate);
		this._config.building.supportBeam.signal.add(this.onUpdate);

		this._config.building.insetBay.front.count.signal.add(this.onUpdate);
		//this._config.building.insetBay.front.drop.signal.add(this.onUpdate);
		this._config.building.insetBay.front.length_1.signal.add(this.onUpdate);
		this._config.building.insetBay.front.length_2.signal.add(this.onUpdate);
		this._config.building.insetBay.back.count.signal.add(this.onUpdate);
		//this._config.building.insetBay.back.drop.signal.add(this.onUpdate);
		this._config.building.insetBay.back.length_1.signal.add(this.onUpdate);
		this._config.building.insetBay.back.length_2.signal.add(this.onUpdate);

		this._config.building.leanto.back.cut_back.signal.add(this.onUpdate);
		this._config.building.leanto.back.cut_front.signal.add(this.onUpdate);
		this._config.building.leanto.back.drop.signal.add(this.onUpdate);
		this._config.building.leanto.back.enabled.signal.add(this.onUpdate);
		this._config.building.leanto.back.wraparound_back.enabled.signal.add(this.onUpdate);
		this._config.building.leanto.back.wraparound_front.enabled.signal.add(this.onUpdate);
		this._config.building.leanto.back.length.signal.add(this.onUpdate);
		this._config.building.leanto.back.pitch.signal.add(this.onUpdate);

		this._config.building.leanto.front.cut_back.signal.add(this.onUpdate);
		this._config.building.leanto.front.cut_front.signal.add(this.onUpdate);
		this._config.building.leanto.front.drop.signal.add(this.onUpdate);
		this._config.building.leanto.front.enabled.signal.add(this.onUpdate);
		this._config.building.leanto.front.wraparound_back.enabled.signal.add(this.onUpdate);
		this._config.building.leanto.front.wraparound_front.enabled.signal.add(this.onUpdate);
		this._config.building.leanto.front.length.signal.add(this.onUpdate);
		this._config.building.leanto.front.pitch.signal.add(this.onUpdate);

		this._config.building.leanto.left.cut_back.signal.add(this.onUpdate);
		this._config.building.leanto.left.cut_front.signal.add(this.onUpdate);
		this._config.building.leanto.left.drop.signal.add(this.onUpdate);
		this._config.building.leanto.left.enabled.signal.add(this.onUpdate);
		this._config.building.leanto.left.wraparound_back.enabled.signal.add(this.onUpdate);
		this._config.building.leanto.left.wraparound_front.enabled.signal.add(this.onUpdate);
		this._config.building.leanto.left.length.signal.add(this.onUpdate);
		this._config.building.leanto.left.pitch.signal.add(this.onUpdate);

		this._config.building.leanto.right.cut_back.signal.add(this.onUpdate);
		this._config.building.leanto.right.cut_front.signal.add(this.onUpdate);
		this._config.building.leanto.right.drop.signal.add(this.onUpdate);
		this._config.building.leanto.right.enabled.signal.add(this.onUpdate);
		this._config.building.leanto.right.wraparound_back.enabled.signal.add(this.onUpdate);
		this._config.building.leanto.right.wraparound_front.enabled.signal.add(this.onUpdate);
		this._config.building.leanto.right.length.signal.add(this.onUpdate);
		this._config.building.leanto.right.pitch.signal.add(this.onUpdate);

	}

	public addToScene(scene: Object3D)
	{
		this._scene = scene;

		this.onUpdate();
	}
	private eraseMeshArray(meshArray: Mesh[])
	{
		for (var i = 0; i < meshArray.length; ++i)
		{
			this._container.remove(meshArray[i]);
		}
		meshArray.length = 0;
	}

	// public get container()
	// {
	// 	return this._container;
	// }
}